import React from 'react';
import { Box, Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import moment from 'moment';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { RootState } from '../../../store';
import KpiCard from '../components/KpiCard';
import ChartPanel from '../components/ChartPanel';
import { CHART_COLORS } from '../components/scoreColors';
import { customError, darkPurple, lightTeal, orange } from '../../../mainTheme';

const fmtDuration = (sec: number) => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const OverviewSection: React.FC = () => {
  const { summary, scores, filters } = useSelector(
    (state: RootState) => state.callStats,
  );
  const s = summary.data;
  const bucketFmt =
    filters.window === '1h' || filters.window === '3h'
      ? 'HH:mm'
      : 'MM/DD HH:mm';

  const typePie = s
    ? [
        { name: 'Video', value: s.byType.video },
        { name: 'SIP', value: s.byType.sip },
      ]
    : [];

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            label="Total calls"
            value={s?.totalCalls ?? '—'}
            accent={darkPurple}
            mocked={summary.mocked}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            label="Video"
            value={s?.videoCalls ?? '—'}
            accent={CHART_COLORS[0]}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            label="SIP / PSTN"
            value={s?.sipCalls ?? '—'}
            accent={lightTeal}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            label="Failed"
            value={s?.failedCalls ?? '—'}
            accent={customError}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            label="Avg duration"
            value={s ? fmtDuration(s.avgDurationSec) : '—'}
            accent={orange}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            label="Avg MOS"
            value={scores.data?.mos != null ? scores.data.mos.toFixed(2) : '—'}
            accent={CHART_COLORS[4]}
            sub="audio"
            mocked={scores.mocked}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={0.5}>
        <Grid item xs={12} md={8}>
          <ChartPanel
            title="Call volume over time"
            subtitle="By type, bucketed across the selected range"
            loading={summary.loading}
            error={summary.error}
            empty={!summary.loading && (!s || s.timeseries.length === 0)}
            mocked={summary.mocked}
            minHeight={280}
          >
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart
                data={s?.timeseries ?? []}
                margin={{ top: 8, right: 12, left: -12, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="bucketStart"
                  tickFormatter={(v) => moment(v).format(bucketFmt)}
                  tick={{ fontSize: 11 }}
                  minTickGap={24}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip
                  labelFormatter={(v) =>
                    moment(v as string).format('MMM D, HH:mm')
                  }
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="video"
                  name="Video"
                  stackId="1"
                  stroke={CHART_COLORS[0]}
                  fill={CHART_COLORS[0]}
                  fillOpacity={0.5}
                />
                <Area
                  type="monotone"
                  dataKey="sip"
                  name="SIP"
                  stackId="1"
                  stroke={lightTeal}
                  fill={lightTeal}
                  fillOpacity={0.5}
                />
                <Area
                  type="monotone"
                  dataKey="failed"
                  name="Failed"
                  stroke={customError}
                  fill={customError}
                  fillOpacity={0.25}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>

        <Grid item xs={12} md={4}>
          <ChartPanel
            title="Calls by type"
            subtitle="Video vs SIP/PSTN"
            loading={summary.loading}
            error={summary.error}
            empty={!summary.loading && (!s || s.totalCalls === 0)}
            mocked={summary.mocked}
            minHeight={280}
          >
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={typePie}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={95}
                  paddingAngle={2}
                >
                  {typePie.map((entry, i) => (
                    <Cell key={entry.name} fill={CHART_COLORS[i]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OverviewSection;
